import { computed, inject, nextTick, onMounted, onUnmounted, provide, ref, watch, type ComputedRef, type InjectionKey, type Ref } from 'vue'
import { API_JT_PROJ_DETAIL } from '@/api/jtProj'
import { useFetch } from './useFetch'
import { useRoute, useRouter } from 'vue-router'
import { isAuth } from '@/directive/permission'
import type { SimpleSideMenuItem } from '@/layout/components'
import { ERROR_403_ROUTE_NAME } from '@/router/constant'
import { mergeReqConditions } from './useConditions'
import { API_JT_ZCH_DETAIL } from '@/api/jtZch'
import { Modal } from 'ant-design-vue'

export const DETAIL_LY_RESULT_KEY: InjectionKey<Ref<any>> = Symbol('DETAIL_LY_RESULT_KEY')
export const DETAIL_LY_LOADING_KEY: InjectionKey<Ref<boolean>> = Symbol('DETAIL_LY_LOADING_KEY')
export const DETAIL_LY_LOADED_KEY: InjectionKey<Ref<boolean>> = Symbol('DETAIL_LY_LOADED_KEY')
export const DETAIL_LY_REFRESH_KEY: InjectionKey<() => void> = Symbol('DETAIL_LY_REFRESH_KEY')
export const DETAIL_LY_HD_HEIGHT_KEY: InjectionKey<Ref<number>> = Symbol('DETAIL_LY_HD_HEIGHT_KEY')
export const DETAIL_LY_HD_HEIGHT_PX_KEY: InjectionKey<ComputedRef<string>> = Symbol('DETAIL_LY_HD_HEIGHT_PX_KEY')

export const DETAIL_LY_ZCH_KEY: InjectionKey<Ref<any>> = Symbol('DETAIL_LY_ZCH_KEY')
export const DETAIL_LY_PROJ_KEY: InjectionKey<Ref<any>> = Symbol('DETAIL_LY_PROJ_KEY')

export const useDetailId = () => {
  const route = useRoute()
  const id = computed(() => route.params.id as string)
  return {
    id
  }
}

export const useProjectId = () => {
  const route = useRoute()
  const pId = computed(() => route.params.pId as string)
  return {
    pId
  }
}

export const useDetailStyles = () => {
  const hdRef = ref<any>(null)
  const hdHeight = ref(0)
  const hdHeightPx = computed(() => `${hdHeight.value}px`)
  const updateHdHeight = () => {
    const el = hdRef.value?.$el as HTMLElement | undefined
    hdHeight.value = el?.offsetHeight ?? 0
  }

  let ro: ResizeObserver | undefined

  onMounted(async () => {
    await nextTick()
    updateHdHeight()

    window.addEventListener('resize', updateHdHeight)

    const el = hdRef.value?.$el as HTMLElement | undefined
    if (el && 'ResizeObserver' in window) {
      ro = new ResizeObserver(updateHdHeight)
      ro.observe(el)
    }
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateHdHeight)
    ro?.disconnect()
  })
  provide(DETAIL_LY_HD_HEIGHT_KEY, hdHeight)
  provide(DETAIL_LY_HD_HEIGHT_PX_KEY, hdHeightPx)
  return {
    hdRef,
    hdHeight,
    hdHeightPx
  }
}
export const initDetailLy = (getFn: BD.PromiseFn = API_JT_PROJ_DETAIL) => {
  const { hdRef, hdHeight, hdHeightPx } = useDetailStyles()
  const { id } = useDetailId()
  const router = useRouter()
  const { loading, loaded, result, fetchData, loadError } = useFetch({
    getFn,
    isShallow: true,
    onError(err) {
      Modal.error({
        title: '获取数据失败',
        content: err.message || '获取数据失败，请稍后重试',
        centered: true,
        onOk() {
          router.replace('/')
        }
      })
    }
  })
  const refresh = () => fetchData(id.value)
  refresh()
  provide(DETAIL_LY_RESULT_KEY, result)
  provide(DETAIL_LY_LOADING_KEY, loading)
  provide(DETAIL_LY_LOADED_KEY, loaded)
  provide(DETAIL_LY_REFRESH_KEY, refresh)
  return {
    hdRef,
    hdHeight,
    hdHeightPx,
    loading,
    loaded,
    loadError,
    result,
    refresh
  }
}

export const useDetailLy = () => {
  const { id } = useDetailId()
  const result = inject(DETAIL_LY_RESULT_KEY)!
  const loading = inject(DETAIL_LY_LOADING_KEY)!
  const loaded = inject(DETAIL_LY_LOADED_KEY)!
  const refresh = inject(DETAIL_LY_REFRESH_KEY)!
  const hdHeight = inject(DETAIL_LY_HD_HEIGHT_KEY)!
  const hdHeightPx = inject(DETAIL_LY_HD_HEIGHT_PX_KEY)!
  return {
    id,
    result,
    loading,
    loaded,
    refresh,
    hdHeight,
    hdHeightPx
  }
}

export const useDetailLyMenus = (menusConfig, refresh) => {
  const menus = computed(() => menusConfig.filter(item => isAuth(item?.permission)))
  const menusMap = computed(() => {
    const _map: Record<string, SimpleSideMenuItem> = {}
    // 递归构建 map
    function buildMap(menus: SimpleSideMenuItem[]) {
      menus.forEach(menu => {
        _map[menu.value] = menu
        if (menu.children?.length) buildMap(menu.children)
      })
    }
    buildMap(menus.value)
    return _map
  })

  const idx = ref<string>()
  const route = useRoute()
  const router = useRouter()
  const updateIdx = () => {
    const _idxStr = route.params?.idx as string
    if (_idxStr) {
      // 校验 idx 是否在菜单中，不在则跳转 403
      if (!menusMap.value[_idxStr]) {
        router.replace({ name: ERROR_403_ROUTE_NAME })
        return
      }
      idx.value = _idxStr
    } else if (menus.value.length > 0) {
      idx.value = menus.value[0].value
    } else {
      router.replace({ name: ERROR_403_ROUTE_NAME })
    }
  }
  watch(
    () => route.params?.idx,
    () => {
      updateIdx()
      refresh()
    },
    { immediate: true }
  )

  const handleMenuChange = e => {
    // Modal.confirm({
    //   title: '切换提醒',
    //   content: '请确认当前编辑内容是否已保存，未保存的内容将会丢失。',
    //   okText: '已保存，继续切换',
    //   cancelText: '取消',
    //   centered: true,
    //   onOk() {
    router.push({ params: { ...route.params, idx: e.key } })
    // }
    // })
  }

  const currentMenuItem = computed(() => (idx.value ? menusMap.value[idx.value] : undefined))

  return {
    menus,
    menusMap,
    handleMenuChange,
    idx,
    currentMenuItem
  }
}
/**
 * 封装详情页分页获取方法，自动添加 projectId 参数
 */
export const useDetailPageGetFn = getFn => {
  const { id } = useDetailId()
  return data =>
    getFn(
      mergeReqConditions(data, [
        {
          conditionKeyName: 'projectId',
          conditionValue: id.value
        }
      ])
    )
}

export const initZchDetailLy = (fetchDetailFn: BD.PromiseFn = API_JT_ZCH_DETAIL, fetchProjectDetailFn: BD.PromiseFn = API_JT_PROJ_DETAIL) => {
  const { hdRef, hdHeight, hdHeightPx } = useDetailStyles()
  const { id } = useDetailId()
  const { pId } = useProjectId()
  const getFn = async () => {
    const [proj, zch] = await Promise.all([fetchProjectDetailFn(pId.value, id.value), fetchDetailFn(id.value)])
    return {
      proj,
      zch
    }
  }
  const router = useRouter()

  const { loading, loaded, result, fetchData, loadError } = useFetch({
    getFn,
    isShallow: true,
    afterGetFn(data) {
      // 校验返回的征拆户详情的projectId是否等于路由上的pId,不等于则跳转403页面
      if (data.zch.projectId !== pId.value) {
        router.replace({ name: ERROR_403_ROUTE_NAME })
      }
    },
    onError(err) {
      Modal.error({
        title: '获取数据失败',
        content: err.message || '获取数据失败，请稍后重试',
        centered: true,
        onOk() {
          router.replace('/')
        }
      })
    }
  })

  const projDetail = computed(() => result.value?.proj)
  const zchDetail = computed(() => result.value?.zch)

  const refresh = () => fetchData(id.value)
  refresh()

  provide(DETAIL_LY_PROJ_KEY, projDetail)
  provide(DETAIL_LY_ZCH_KEY, zchDetail)
  provide(DETAIL_LY_LOADING_KEY, loading)
  provide(DETAIL_LY_LOADED_KEY, loaded)
  provide(DETAIL_LY_REFRESH_KEY, refresh)
  return {
    hdRef,
    hdHeight,
    hdHeightPx,
    loading,
    loaded,
    loadError,
    refresh,
    projDetail,
    zchDetail,
    id,
    pId
  }
}

export const useZchDetailLy = () => {
  const { id } = useDetailId()
  const { pId } = useProjectId()
  const projDetail = inject(DETAIL_LY_PROJ_KEY)!
  const zchDetail = inject(DETAIL_LY_ZCH_KEY)!
  const loading = inject(DETAIL_LY_LOADING_KEY)!
  const loaded = inject(DETAIL_LY_LOADED_KEY)!
  const refresh = inject(DETAIL_LY_REFRESH_KEY)!
  const hdHeight = inject(DETAIL_LY_HD_HEIGHT_KEY)!
  const hdHeightPx = inject(DETAIL_LY_HD_HEIGHT_PX_KEY)!
  return {
    id,
    pId,
    projDetail,
    zchDetail,
    loading,
    loaded,
    refresh,
    hdHeight,
    hdHeightPx
  }
}

interface useZchDetailPageGetFnOptions {
  getFn: BD.PromiseFn
  // 是否自动添加 projectId 参数，默认 true
  autoProjectId?: boolean
  // 是否自动添加 householdId 参数，默认 true
  autoHouseholdId?: boolean
  // 其他额外参数
  extraParams?: Partial<BasicCondition>[]
}
/**
 * 封装集体土地征拆户详情页分页获取方法，自动添加参数
 */
export const useZchDetailPageGetFn = (options: useZchDetailPageGetFnOptions) => {
  const { getFn, autoProjectId = true, autoHouseholdId = true, extraParams = [] } = options
  const { id } = useDetailId()
  const { pId } = useProjectId()
  const parmas: Partial<BasicCondition>[] = [...extraParams]
  if (autoProjectId) {
    parmas.push({
      conditionKeyName: 'projectId',
      conditionValue: pId.value
    })
  }
  if (autoHouseholdId) {
    parmas.push({
      conditionKeyName: 'householdId',
      conditionValue: id.value
    })
  }
  return data => getFn(mergeReqConditions(data, parmas))
}
interface useZchDetailCommonFnOptions {
  householdIdKey?: string
  projectIdKey?: string
}
export const useZchDetailCommonFn = (options: useZchDetailCommonFnOptions = {}) => {
  const { householdIdKey = 'householdId', projectIdKey = 'projectId' } = options
  const { id } = useDetailId()
  const { pId } = useProjectId()

  const commonParams = { [householdIdKey]: id.value!, [projectIdKey]: pId.value! }
  const decorateFn = fn => data =>
    fn({
      ...data,
      ...commonParams
    })
  return {
    decorateFn
  }
}
